import { Trash2, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const DeleteAccountModal = () => {
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [error, setError] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const deleteAccountMutation = useMutation({
    mutationKey: ["deleteAccount"],
    mutationFn: (data) => {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No token found");
      }
      
      axiosInstance.defaults.headers.jwt = token;
      
      return axiosInstance.post("api/deleteAccount", data);
    },
    onSuccess: () => {
      toast.success("Account deleted successfully");
      setTimeout(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }, 2000);
    },
    onError: (err) => {
      setError(err?.response?.data?.message || "Failed to delete account");
      toast.error(err?.response?.data?.message || "Failed to delete account");
    }
  });

  const openDeleteModal = () => {
    setIsModalVisible(true);
  };

  const closeDeleteModal = () => {
    setIsModalVisible(false);
    setIsConfirmed(false);
    setError(null);
  };

  const handleDeleteAccount = () => {
    if (!isConfirmed) {
      setError("Please confirm that you want to delete your account.");
      return;
    }

    const userData = JSON.parse(localStorage.getItem("user")) || {};
    
    deleteAccountMutation.mutate({
      Name: userData.user_name || "",
      Email: userData.email || "",
      Message: "User requested account deletion"
    });
  };

  return (
    <div className="flex items-center justify-between p-2 w-full bg-transparent my-5">
      <span className="text-black font-medium text-xl">Delete Account?</span>
      <button
        onClick={openDeleteModal}
        className="px-4 text-xl py-3 rounded-xl font-semibold bg-black text-red-500"
      >
        Delete Account
      </button>

      {isModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-3/4 max-w-md">
            <Trash2 color="red" />
            <h2 className="text-2xl font-semibold mb-4 mt-4">
              Delete account?
            </h2>
            <p className="text-gray-600 mb-4">
              Your profile and all data related to it will be permanently
              deleted, and you won't be able to recover them.
            </p>

            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="confirmDelete"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="confirmDelete" className="text-gray-700 my-5">
                I confirm I want to delete my account
              </label>
            </div>

            <div className="flex justify-between gap-10">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteAccountMutation.isPending || !isConfirmed}
                className={`bg-gray-900 text-red-600 px-6 py-2 rounded-xl flex-1 ${
                  deleteAccountMutation.isPending || !isConfirmed
                    ? "opacity-80 cursor-not-allowed"
                    : ""
                }`}
              >
                {deleteAccountMutation.isPending ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Deleting...
                  </span>
                ) : (
                  "Delete Account"
                )}
              </button>
              <button
                onClick={closeDeleteModal}
                className="px-6 py-2 rounded-xl flex-1 border-2 text-purple-400 border-purple-400"
              >
                Cancel
              </button>
            </div>

            {error && <div className="text-red-500 mt-2">{error}</div>}
            {deleteAccountMutation.isSuccess && (
              <div className="text-green-500 mt-2">
                Account deleted successfully. Redirecting...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountModal;
